<template>
  <v-dialog v-model="open" max-width="600px" persistent :transition="false">
    <template #default>
      <v-card>
        <!-- DEBUG PANEL - REMOVE THIS BLOCK WHEN DEBUGGING IS COMPLETE -->
        <v-card-text
          v-if="showDebugPanel"
          class="pa-2 bg-yellow-lighten-4 border-b"
        >
          <div class="text-caption">
            <strong>🐛 DEBUG:</strong>
            Type: <code>{{ formsStore.type }}</code> | ID:
            <code>{{ formsStore.item?.id || "null" }}</code> | Operation:
            <code>{{ formsStore.item?.id ? "EDIT" : "CREATE" }}</code> |
            FormKey: <code>{{ formsStore.formKey }}</code>
          </div>
          <details class="mt-1">
            <summary class="text-caption cursor-pointer">
              Show full item data
            </summary>
            <pre class="text-xs mt-1 pa-2 bg-grey-lighten-5 rounded">{{
              JSON.stringify(formsStore.item, null, 2)
            }}</pre>
          </details>
        </v-card-text>
        <!-- END DEBUG PANEL -->

        <v-card-title
          class="bg-grey-lighten-3 d-flex justify-space-between align-center"
        >
          <span>{{ type_label }}</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="resetForm"
          />
        </v-card-title>
        <v-card-text>
          <component
            :is="componentToRender"
            :key="formsStore.formKey"
            v-bind="componentProps"
            @report-created="handleFormSaved"
            @saved="handleFormSaved"
            @update="handleFormSaved"
            @created="handleFormSaved"
            @uploaded="handleFormSaved"
            @completed="handleFormSaved"
          />
        </v-card-text>
      </v-card>
    </template>
  </v-dialog>
</template>

<script>
import { computed, defineComponent } from "vue";
import { useTenantStore } from "@/stores/tenantStore";
import ReportFormRefactorSimple from "../reports/ReportFormRefactorSimple.vue";
import DayReportForm from "../day_reports/DayReportForm.vue";
import DayReportTypeForm from "../day_report_types/DayReportTypeForm.vue";
import DayReportLineForm from "../day_report_lines/DayReportLineForm.vue";
import ImageUpload from "../ui/ImageUpload.vue";
import CommentBox from "../comments/CommentBox.vue";
import DeadlineForm from "../deadlines/DeadlineForm.vue";
import ContactForm from "../contacts/ContactForm.vue";
import ContactDetailForm from "../contact_details/ContactDetailForm.vue";
import UserPasswordForm from "../users/UserPasswordForm.vue";
import UserForm from "../users/UserForm.vue";
import AccountForm from "../accounts/AccountForm.vue";
import { useFormsStore } from "@/stores/formsStore";
//import { useCommentsStoreRefactor } from "@/stores/commentsStoreRefactor";
import DayReportTypeFormSimple from "../day_report_types/DayReportTypeForm.vue";

export default defineComponent({
  name: "FormHolder",
  components: {
    ReportForm: ReportFormRefactorSimple,
    DayReportForm,
    DayReportTypeForm,
    DayReportLineForm,
    ImageUpload,
    CommentBox,
    DeadlineForm,
    ContactForm,
    ContactDetailForm,
    UserPasswordForm,
    UserForm,
    AccountForm,
    DayReportTypeFormSimple,
  },
  emits: ["report-updated", "saved", "created", "updated"],
  setup(props, { emit }) {
    const formsStore = useFormsStore();
    // const commentsStore = useCommentsStoreRefactor();

    const open = computed(() => formsStore.open);
    const tenantStore = useTenantStore();
    const showDebugPanel = computed(() => tenantStore.isDebugMode);

    // Initialize CommentsStore when showing comments
    // watch(
    //   () => formsStore.type,
    //   (newType) => {
    //     if (newType === "comments") {
    //       console.log(
    //         "FormHolder: Comment form opened, initializing CommentsStore",
    //       );
    //       commentsStore.init();
    //     }
    //   },
    //   { immediate: true },
    // );

    // // Also check on mount
    // onMounted(() => {
    //   if (formsStore.type === "comments") {
    //     console.log(
    //       "FormHolder: Comment form mounted, initializing CommentsStore",
    //     );
    //     commentsStore.init();
    //   }
    // });

    // Reset the processing flag when form opens

    const type_label = computed(() => {
      const storeType = formsStore.type;
      switch (storeType) {
        case "report":
          return "Verslag";
        case "day_report":
          return "Veldwerk verslag";
        case "day_report_type":
          return "Veldwerk verslag type";
        case "day_report_line":
          return "Veldwerk verslag regel";
        case "image":
          return "Afbeelding uploaden";
        case "comments":
          return "Reacties";
        case "deadline":
          return "Deadline";
        case "contact":
          return "Contact";
        case "contact_detail":
          return "Contact detail";
        case "user_password":
          return "Wachtwoord wijzigen";
        case "user":
          return "Gebruiker";
        case "account":
          return "DG beheren";
        default:
          return "";
      }
    });

    const componentToRender = computed(() => {
      const storeType = formsStore.type;
      switch (storeType) {
        case "report":
          return ReportFormRefactorSimple;
        case "day_report":
          return DayReportForm;
        case "day_report_type":
          return DayReportTypeForm;
        case "day_report_line":
          return DayReportLineForm;
        case "image":
          return ImageUpload;
        case "comments":
          return CommentBox;
        case "deadline":
          return DeadlineForm;
        case "contact":
          return ContactForm;
        case "contact_detail":
          return ContactDetailForm;
        case "user_password":
          return UserPasswordForm;
        case "user":
          return UserForm;
        case "account":
          return AccountForm;
        default:
          return null;
      }
    });

    const componentProps = computed(() => {
      // Only access store properties we actually need to avoid unnecessary reactivity
      const storeType = formsStore.type;
      const storeItem = formsStore.item;
      const storeOptions = formsStore.options;

      // Debug log to help troubleshoot missing props
      console.log("FormHolder - rendering:", storeType, {
        item: storeItem,
        options: storeOptions,
      });

      // Create a clean copy for account to avoid reactive proxies
      let cleanItem = null;
      if (storeType === "account") {
        cleanItem = JSON.parse(JSON.stringify(storeItem));
      }

      switch (storeType) {
        case "day_report_type":
          return {
            day_report_type: storeItem,
            popup: true,
          };
        case "contact":
          return {
            contact: storeItem,
            popup: true,
          };
        case "day_report":
          return {
            day_report: storeItem,
            popup: true,
            ...storeOptions,
          };
        case "day_report_line":
          return {
            item: storeItem,
            popup: true,
            ...storeOptions,
          };
        case "report":
          return {
            report: storeItem,
            popup: true,
            ...storeOptions,
          };
        case "comments":
          return {
            type: storeItem.type,
            id: storeItem.id,
            // Pass the report object if available (for displaying in comment dialog)
            ...(storeItem.report && { report: storeItem.report }),
            popup: true,
          };
        case "user":
          return {
            user: storeItem,
            popup: true,
          };
        case "account":
          return {
            accountProp: cleanItem,
            popup: true,
            ...storeOptions,
          };
        case "image":
          return {
            item: {
              ...storeItem,
              // Ensure we have the correct ID for existing images
              id: storeItem.id || null,
              // Make sure file and file_dl are correctly passed
              file: storeItem.file || null,
              file_dl: storeItem.file_dl || null,
            },
            recordType: storeItem.recordType || storeOptions.recordType,
            recordId: storeItem.recordId || storeOptions.recordId,
            field: storeItem.field || storeOptions.field,
            filetype: storeOptions.filetype || "image/png, image/jpeg",
            onUploadComplete: storeOptions.onComplete,
            popup: true,
          };
        case "deadline":
          return {
            deadline: storeItem,
            options: storeOptions,
            popup: true,
          };
        case "user_password":
          return {
            user: storeItem,
            popup: true,
          };
        default:
          return {
            ...storeItem,
            ...storeOptions,
            popup: true,
          };
      }
    });

    const resetForm = () => {
      formsStore.$reset();
    };

    // Generic handler for all form save events
    const handleFormSaved = (result) => {
      emit("saved", result);
      formsStore.$reset();
    };

    return {
      open,
      type_label,
      componentToRender,
      componentProps,
      resetForm,
      handleFormSaved,
      formsStore,
      showDebugPanel,
    };
  },
});
</script>
